import { Article, Quiz } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Chip,
  Divider,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";

import type { IFeatures } from "@/utils/interfaces";

import { UserMenuItemRow } from "./UserMenuItemRow";
import { UserMenuLogoutRow } from "./UserMenuLogoutRow";

interface UserMenuPanelContentProps {
  userName?: string | null;
  userUsername?: string | null;
  accessLabel: string;
  initials: string;
  menuItems: IFeatures[];
  showProfileHero?: boolean;
  onNavigate: (path?: string) => void;
  onOpenSupport: () => void;
  onOpenTerms: () => void;
  onLogout: () => void;
}

export function UserMenuPanelContent({
  userName,
  userUsername,
  accessLabel,
  initials,
  menuItems,
  showProfileHero = false,
  onNavigate,
  onOpenSupport,
  onOpenTerms,
  onLogout,
}: UserMenuPanelContentProps) {
  const theme = useTheme();

  return (
    <Stack spacing={1.25}>
      {showProfileHero ? (
        <Stack
          alignItems="center"
          spacing={1}
          sx={{
            py: 1.5,
            px: 1,
            borderRadius: 2.5,
            bgcolor: alpha(theme.palette.primary.main, 0.05),
          }}
        >
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: "primary.main",
              color: "primary.contrastText",
              fontWeight: 700,
              fontSize: "1.25rem",
            }}
          >
            {initials}
          </Avatar>
          <Box sx={{ textAlign: "center", minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.25}>
              {userName || "Usuário"}
            </Typography>
            {userUsername ? (
              <Typography variant="caption" color="text.secondary">
                {userUsername}
              </Typography>
            ) : null}
          </Box>
          <Chip
            size="small"
            label={accessLabel}
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600, height: 24 }}
          />
        </Stack>
      ) : null}

      {menuItems.length > 0 ? (
        <Stack spacing={0.25}>
          <Typography
            variant="overline"
            color="text.disabled"
            sx={{ px: 0.5, letterSpacing: 0.8, fontSize: "0.65rem" }}
          >
            Conta
          </Typography>
          {menuItems.map((item, index) => (
            <UserMenuItemRow
              key={`user-menu-item-${item.path}-${index}`}
              icon={item.icon}
              label={item.avatarMenuText || item.text}
              onClick={() => onNavigate(item.path)}
            />
          ))}
        </Stack>
      ) : null}

      <Divider sx={{ my: 0.25 }} />

      <Stack spacing={0.25}>
        <Typography
          variant="overline"
          color="text.disabled"
          sx={{ px: 0.5, letterSpacing: 0.8, fontSize: "0.65rem" }}
        >
          Ajuda
        </Typography>
        <UserMenuItemRow
          icon={Quiz}
          label="Feedback e suporte"
          onClick={onOpenSupport}
        />
        <UserMenuItemRow
          icon={Article}
          label="Termos de uso"
          onClick={onOpenTerms}
        />
      </Stack>

      <Divider sx={{ my: 0.5 }} />
      <UserMenuLogoutRow onClick={onLogout} />
    </Stack>
  );
}
